import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Image,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CategoryKey, heroBanners } from '../../data/services';
import {
  colors,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../../theme';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import HomeHeader from './HomeHeader';
import SearchBar from './SearchBar';

/**
 * The block that opens the home screen: the artwork is the background, and
 * everything else sits on it.
 *
 * The banner is not a card inside the hero - it *is* the hero. The nav, the
 * location and the search field are laid over the picture so the whole top of
 * the screen reads as one surface rather than as a tinted panel containing a
 * photograph.
 *
 * Copy travels with its own page rather than living in the overlay: it belongs
 * to the artwork underneath it, and left in the fixed layer it would sit still
 * while the picture it describes slid away.
 *
 * The banner advances on its own and can be swiped, and the two cooperate -
 * the timer is cleared the moment a drag starts and only rearmed once the
 * scroll settles, so an automatic advance can never fight the finger.
 */

/** How long each banner is held before the rail advances on its own. */
const DWELL_MS = 4500;

/**
 * Time to leave the rail alone after a manual swipe. Resuming the moment a
 * drag ends yanks the banner out from under someone still reading it.
 */
const RESUME_MS = 7000;

/**
 * Room the nav needs at the top of the picture.
 *
 * One row, not two: the address moved up beside the logo, so the band no
 * longer has to reserve a second line under it. Fixed on every device - it
 * holds controls, and a control is sized by the finger that presses it.
 */
const NAV_HEIGHT = 76;
/**
 * Room the search field needs, directly under the location. Also a control.
 *
 * Taller than the field itself on purpose: everything below this band is the
 * banner's own tap target, and at 66 the three controls - location, search,
 * banner - sat close enough that a thumb aimed at one landed on another. The
 * extra points are breathing room between the search field and the banner
 * copy, which is what makes each tappable without hitting its neighbour.
 */
const SEARCH_BLOCK = 78;
/**
 * Room the banner's own copy needs, under the search field.
 *
 * The one block here that must move with the device. Nav + search + copy came
 * to a fixed 276pt, which is a third of a 844pt phone and very nearly half of
 * a 568pt one - on a small screen the categories started below the fold and
 * the page opened on a picture. Scaled off the height (not the width: this is
 * vertical space competing with the rest of the page) it gives back about
 * 30pt there, and takes a little more on a tall phone where there is room.
 *
 * The copy inside is clamped to two lines against exactly this number, so
 * anything that changes it has to be checked against a long headline.
 */
const COPY_BLOCK = 148;

/** A banner as the server publishes it: a picture, and where it goes. */
export type PublishedBanner = {
  imageUrl: string;
  accessibilityLabel?: string;
  action?: { type: string; target: string };
  /**
   * The overlay copy, when the banner does not carry its own.
   *
   * A promotion whose NUMBER changes - "25% off" becoming "20% off" - should
   * be editable without a designer and a re-upload, so the words can live
   * beside the picture rather than inside it. `headline` switches the overlay
   * on; a banner whose artwork already says everything simply omits all three
   * and gets no words drawn over it.
   */
  eyebrow?: string;
  headline?: string;
  cta?: string;
};

type Props = {
  address: string;
  hints: string[];
  onPressAddress?: () => void;
  onPressNotifications?: () => void;
  onPressWallet?: () => void;
  onPressSearch?: () => void;
  onPressBanner?: (category?: CategoryKey) => void;
  notificationCount?: number;
  /**
   * Banners from the published home configuration, when there are any.
   *
   * ---------------------------------------------------------------------------
   * THE HERO IS THE BANNER SLOT, SO THE TREE FEEDS IT RATHER THAN REPLACING IT
   * ---------------------------------------------------------------------------
   * The address bar, the search field and the nav sit ON the artwork - the
   * banner is not a card inside the hero, it IS the hero, as the note at the
   * top of this file says. So a published `banner_carousel` is lifted out of
   * the tree by `HomeScreen` and handed here, and the rest of the tree renders
   * below through `ConfigRenderer`.
   *
   * Empty or absent means the compiled `heroBanners` are drawn, unchanged.
   * That is the whole fallback story for this screen: no publication, an old
   * build, a failed request and a malformed tree all end up in the same place,
   * and the same place is the home screen that shipped.
   */
  banners?: PublishedBanner[];
  /** Where a published banner's action leads. Ignored for compiled ones. */
  onPressPublishedBanner?: (action?: { type: string; target: string }) => void;
};

function HomeHero({
  address,
  hints,
  onPressAddress,
  onPressNotifications,
  onPressWallet,
  onPressSearch,
  onPressBanner,
  notificationCount = 0,
  banners,
  onPressPublishedBanner,
}: Props) {
  const resp = useResponsive();
  const window = useWindowDimensions();

  /**
   * One shape for both sources, so everything below this line - the pager, the
   * auto-advance, the dots, the accessibility labels - is written once.
   *
   * A published slide has no `headline`, and that absence is what switches the
   * copy overlay off: its words are in the picture. See the note where the
   * overlay is drawn.
   */
  const slides = useMemo(
    () =>
      banners?.length
        ? banners.map((banner, index) => ({
            key: `published-${index}-${banner.imageUrl}`,
            image: { uri: banner.imageUrl },
            eyebrow: banner.eyebrow,
            headline: banner.headline,
            cta: banner.cta,
            /*
             * The words if there are any, and an honest placeholder if there
             * are not.
             *
             * A banner with its copy printed into the artwork leaves a screen
             * reader nothing to read, and "Offer" says a tappable promotion is
             * here without inventing what it says. A publication should carry
             * an `accessibilityLabel` in that case, and this is what happens
             * when nobody wrote one.
             */
            accessibilityLabel:
              banner.accessibilityLabel ||
              [banner.eyebrow, banner.headline, banner.cta]
                .filter(Boolean)
                .join('. ') ||
              'Offer',
            onPress: () => onPressPublishedBanner?.(banner.action),
          }))
        : heroBanners.map(banner => ({
            key: banner.key,
            image: banner.image,
            eyebrow: banner.eyebrow,
            headline: banner.headline,
            cta: banner.cta,
            accessibilityLabel: `${banner.eyebrow}. ${banner.headline}. ${banner.cta}`,
            onPress: () => onPressBanner?.(banner.category),
          })),
    [banners, onPressBanner, onPressPublishedBanner],
  );

  /*
   * The hero's own width, not the window's.
   *
   * The pager is paged by this number - each banner is one `width` wide and
   * the rail scrolls by multiples of it - so it has to be the width of the
   * container the pager is actually laid out in. On a tablet Home narrows
   * itself to a centred column, and a hero paging by the full window width
   * would land every banner part-way off its own frame.
   *
   * Seeded with the window width rather than 0 so the first frame draws the
   * banner at very nearly the right size instead of at nothing; the measured
   * value replaces it on the next.
   */
  const [width, setWidth] = useState(window.width);
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const measured = event.nativeEvent.layout.width;
    setWidth(prev => (Math.abs(prev - measured) > 0.5 ? measured : prev));
  }, []);

  const scrollRef = useRef<ScrollView>(null);
  const [index, setIndex] = useState(0);

  /** Mirrors `index` for the timer, which must not be rebuilt on each tick. */
  const indexRef = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /*
   * The status bar is part of the picture, not a band above it.
   *
   * `styles.hero` has always said it "meets the status bar" - only the bottom
   * corners are rounded - but the shell wrapped this page in a `SafeAreaView`
   * that claimed the top edge, so the inset was painted in the page background
   * and the artwork started below it. That flat strip above a rounded hero is
   * the thing that read as a white bar.
   *
   * Home drops that edge (see `HomeScreen`) and the inset is spent here
   * instead: the box grows by it so the art fills the taller frame, and the
   * overlay below pads its controls back down. Background bleeds, content does
   * not.
   */
  const insets = useSafeAreaInsets();
  const height =
    insets.top + NAV_HEIGHT + resp.vscale(COPY_BLOCK, 0.6) + SEARCH_BLOCK;

  const stop = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const arm = useCallback(
    (delay: number) => {
      stop();
      if (slides.length < 2) {
        return;
      }
      timer.current = setTimeout(() => {
        const next = (indexRef.current + 1) % slides.length;
        indexRef.current = next;
        setIndex(next);
        scrollRef.current?.scrollTo({ x: next * width, animated: true });
        arm(DWELL_MS);
      }, delay);
    },
    [stop, width],
  );

  useEffect(() => {
    arm(DWELL_MS);
    return stop;
  }, [arm, stop]);

  /*
   * A rotation - or a fold opening - changes the page width, which leaves the
   * pager parked between two banners at the old stride. Snapping back to the
   * current index un-animated is invisible if nothing changed, and is the
   * difference between a hero and two half-heroes if something did.
   */
  const lastWidth = useRef(width);
  useEffect(() => {
    if (lastWidth.current === width) {
      return;
    }
    lastWidth.current = width;
    scrollRef.current?.scrollTo({
      x: indexRef.current * width,
      animated: false,
    });
  }, [width]);

  const onSettled = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const landed = Math.round(event.nativeEvent.contentOffset.x / width);
      indexRef.current = landed;
      setIndex(landed);
      arm(RESUME_MS);
    },
    [arm, width],
  );

  return (
    <View style={[styles.hero, { height }]} onLayout={onLayout}>
      {/* The pictures, full bleed, one screen wide each. */}
      <ScrollView
        ref={scrollRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onScrollBeginDrag={stop}
        onMomentumScrollEnd={onSettled}
        // A slow drag can end without momentum, which leaves the timer off.
        onScrollEndDrag={() => arm(RESUME_MS)}
      >
        {slides.map(slide => (
          <Pressable
            key={slide.key}
            accessibilityRole="button"
            accessibilityLabel={slide.accessibilityLabel}
            onPress={slide.onPress}
            style={{ width, height }}
          >
            <Image source={slide.image} style={styles.art} resizeMode="cover" />

            {/*
              Only on a compiled banner.

              A published one carries its words INSIDE the artwork - the server
              stores an image and an optional action, nothing else - which is
              how remote banners have to work: an overlay drawn from separate
              copy can only ever disagree with the picture behind it, and the
              note on `heroBanners` already warned that re-shooting the artwork
              means moving the copy with it. Baking them together removes that
              problem rather than managing it.

              Held to half the width and sat between the nav and the search
              field. The artwork's left half is empty wall and its right half is
              furniture - copy that runs past the midpoint lands on the sofa and
              stops being readable.
            */}
            {/* Any of the three switches the overlay on, not the headline
                alone. Ops filled an eyebrow and a button and left the headline
                empty - and saw neither, because the headline was the only
                switch. Each line below still draws only if it was typed. */}
            {slide.eyebrow || slide.headline || slide.cta ? (
              <View
                style={[
                  styles.copy,
                  {
                    top: insets.top + NAV_HEIGHT + SEARCH_BLOCK,
                    /* The hero background bleeds to the window's edges on the
                       web (see HomeScreen), but the words stay on the centred
                       content column: pushed in by its inset and capped at
                       the same share of IT that `styles.copy` takes of a
                       phone. Both are no-ops on a phone, where the inset is 0
                       and the column is the screen. */
                    left: resp.sideInset,
                    maxWidth: resp.contentWidth * 0.54,
                  },
                ]}
              >
                {/*
                  Each of the three stands on its own.
                  ---------------------------------------------------------
                  `headline` switches the overlay on, and the other two used
                  to be drawn whether or not they had anything in them. Both
                  are optional in the admin console, so a banner with only a
                  headline - the obvious thing to write first - got a blank
                  line above it and, worse, an EMPTY GREEN PILL below it with
                  nothing but an arrow in it. The picture is allowed to carry
                  its own eyebrow and its own button; only the words that were
                  actually typed should be drawn over it.
                */}
                {slide.eyebrow ? (
                  <Text
                    variant="labelSm"
                    color={colors.inkMuted}
                    style={styles.eyebrow}
                    numberOfLines={1}
                  >
                    {slide.eyebrow}
                  </Text>
                ) : null}
                {/* Clamped, because `COPY_BLOCK` is a fixed height that fits
                    two lines. A headline whose line wraps renders three and
                    pushes the button down onto the dots - truncating is the
                    lesser failure, and it makes overlong copy obvious in
                    review. */}
                {slide.headline ? (
                  <Text
                    variant="titleLg"
                    color={colors.ink}
                    style={styles.headline}
                    numberOfLines={2}
                  >
                    {slide.headline}
                  </Text>
                ) : null}

                {/* A view, not a Pressable - the whole banner is the target,
                    and a nested one would swallow the tap on its most obvious
                    part. Absent when no label was written: a pill holding only
                    an arrow reads as a broken button, and the banner is
                    tappable with or without one. */}
                {slide.cta ? (
                  <View style={styles.cta}>
                    {/* `includeFontPadding` off, or Android centres the label
                        against the font's full metrics and it sits visibly low
                        in the pill next to the arrow. */}
                    <Text
                      variant="labelSm"
                      color={colors.onPrimary}
                      style={styles.ctaLabel}
                    >
                      {slide.cta}
                    </Text>
                    <Icon
                      name="arrowRight"
                      size={14}
                      color={colors.onPrimary}
                      strokeWidth={2}
                    />
                  </View>
                ) : null}
              </View>
            ) : null}
          </Pressable>
        ))}
      </ScrollView>

      {/*
        The fixed layer. `box-none` so it catches taps on its own controls and
        lets everything else through to the pager underneath - without it the
        banner could not be swiped anywhere the overlay covers, which is most
        of the hero.
      */}
      <View
        style={[styles.overlay, { paddingTop: insets.top }]}
        pointerEvents="box-none"
      >
        {/* The inner half of the bleed split (see HomeScreen): the artwork
            behind runs to the window's edges, the controls hold the centred
            content column. Width and cap are equal on a phone, so this
            wrapper is invisible there. */}
        <View
          style={[styles.overlayColumn, { maxWidth: resp.contentWidth }]}
          pointerEvents="box-none"
        >
          <HomeHeader
            address={address}
            onPressAddress={onPressAddress}
            onPressNotifications={onPressNotifications}
            onPressWallet={onPressWallet}
            notificationCount={notificationCount}
          />

          {/* Directly under the location: the address decides what the search
            can return, so the two belong together. The banner's own copy sits
            below them both. */}
          <View style={styles.search}>
            <SearchBar hints={hints} onPress={onPressSearch} />
          </View>

          <View style={styles.spacer} pointerEvents="none">
            <View
              style={styles.dots}
              accessible
              accessibilityRole="progressbar"
              accessibilityLabel={`Offer ${index + 1} of ${slides.length}`}
            >
              {slides.map((slide, i) => (
                <View
                  key={slide.key}
                  style={[styles.dot, i === index ? styles.dotActive : null]}
                />
              ))}
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  hero: {
    // Meets the status bar, so only the bottom corners are rounded.
    borderBottomLeftRadius: radii.xl,
    borderBottomRightRadius: radii.xl,
    overflow: 'hidden',
    backgroundColor: colors.surfaceAccent,
  },
  /* Percentages, not `absoluteFill`: an Image given only insets can fall back
     to its intrinsic size instead of filling. */
  art: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  /* Below the nav and the search field, both of which are drawn in the fixed
     overlay above this page. */
  copy: {
    position: 'absolute',
    left: 0,
    // `top` is applied per-render: it tracks the safe-area inset the overlay
    // above pads by, so the copy stays square under the search field.
    width: '54%',
    paddingLeft: screenPadding,
    gap: 2,
  },
  eyebrow: {
    marginBottom: 2,
  },
  /* The weight lives in `titleLg` now - this only sets the gap under it. */
  headline: {
    marginBottom: spacing.md,
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: radii.pill,
    backgroundColor: colors.primary,
  },
  /* Keeps the label on the pill's true vertical centre - see the render. */
  ctaLabel: {
    includeFontPadding: false,
    textAlignVertical: 'center',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  /* The centred column the overlay's controls hold - max width is set
     per-render from `resp.contentWidth`. See the render note. */
  overlayColumn: {
    flex: 1,
    width: '100%',
    alignSelf: 'center',
  },
  /** Takes the space between the nav and the search field. */
  spacer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  dots: {
    flexDirection: 'row',
    alignSelf: 'center',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.md,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    opacity: 0.8,
  },
  dotActive: {
    width: 18,
    opacity: 1,
    backgroundColor: colors.primary,
  },
  /* `md`, not `sm` - part of the same de-crowding as `SEARCH_BLOCK`: the
     location chip above and the field are both controls, and eight points
     between two tap targets is a mis-tap, not a gap. */
  search: {
    marginTop: spacing.md,
  },
});

export default React.memo(HomeHero);
